import React, { useCallback, useMemo, useState, useEffect } from "react";
import PermissionGroupCard from "../PermissionGroupCard";
import { PermissionsListProps } from "./types";
import { PERMISSIONS } from "constants/permission";
import styles from "./styles.module.scss";
import Text from "components/general/Text";
import Button from "components/general/Button";
import showSuccessMsg from "utils/showSuccessMsg";
import { useUpdateRolePermissionsMutation } from "apis/services/permissions";
import HandleErrors from "utils/handleErrors";
import InfoCard from "components/general/InfoCard";

const EMPTY_ARRAY: any[] = [];

const PermissionsList = React.memo(function PermissionsList({
  id,
  name,
  isDefault,
  permissions = EMPTY_ARRAY,
  containerClassName,
  showSaveButton = true,
  onChange,
  isSuperUser
}: PermissionsListProps) {

  const [updateRolePermissions, { isLoading: isUpdatingRolePermissions }] = useUpdateRolePermissionsMutation();

  // Normalize initial value: if it's objects, extract ids.
  const initialIds = useMemo(
    () => {
      if (isSuperUser) {
        return Object.values(PERMISSIONS).flatMap(group => group.map(p => p.id));
      }
      return permissions?.map((p: any) => (typeof p === "number" || typeof p === "string" ? Number(p) : p.id)).filter(Boolean) || [];
    },
    [permissions, isSuperUser]
  );

  // Local state for selected ids
  const [selectedIds, setSelectedIds] = useState<number[]>(initialIds);

  // Sync state when initial value changes
  useEffect(() => {
    setSelectedIds(initialIds);
  }, [initialIds]);

  useEffect(() => {
    onChange?.(selectedIds);
  }, [selectedIds]);


  const handleChange = useCallback((id: number, enabled: boolean) => {
    setSelectedIds((prev) =>
      enabled ? [...prev, id] : prev.filter((c) => c !== id)
    );
  }, []);

  const handleSelectAll = useCallback((groupIds: number[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      groupIds.forEach((c) => next.add(c));
      return Array.from(next);
    });
  }, []);

  const handleDeselectAll = useCallback((groupIds: number[]) => {
    const removeSet = new Set(groupIds);
    setSelectedIds((prev) => prev.filter((c) => !removeSet.has(c)));
  }, []);

  const handleSave = useCallback(() => {
    if (!id) return;
    console.log("Saving permissions:", selectedIds);
    const values = {
      id,
      name,
      permissions: selectedIds,
    }
    updateRolePermissions(values).unwrap().then(() => {
      showSuccessMsg({ msg: "Permissions saved successfully" });
    }).catch(HandleErrors);
  }, [id, name, selectedIds, updateRolePermissions]);

  return (
    <div className={`${styles.container} ${containerClassName || ""}`}>
      {isSuperUser &&
        <InfoCard
          title="Super User"
          description="Users with this role have all permissions by default. You cannot disable any permissions for super users. They also have permissions over all roles, including permissions that are not explicitly listed or configurable here."
          variant="warning"
        />
      }
      <div className={styles.header}>
        <div className={styles.headerTexts}>
          <Text className={styles.title}>System Permissions</Text>
          <Text className={styles.subtitle}>
            Select the system actions users with this role are allowed to perform.
          </Text>
        </div>
        {showSaveButton && !isSuperUser && (
          <div className={styles.actionButtons}>
            <Button btnClassName={styles.btn} isLoading={isUpdatingRolePermissions} onClick={handleSave}>
              Save
            </Button>
          </div>
        )}
      </div>

      <div className={styles.cardsContainer}>
        {Object.entries(PERMISSIONS).map(([groupKey, items]) => (
          <PermissionGroupCard
            key={groupKey}
            title={groupKey.replace(/_/g, " ")}
            permissions={items}
            selectedIds={selectedIds}
            onChange={handleChange}
            onSelectAll={() => handleSelectAll(items.map((i) => i.id))}
            onDeselectAll={() => handleDeselectAll(items.map((i) => i.id))}
            disabled={isSuperUser}
          />
        ))}
      </div>
    </div>
  );
});

export default PermissionsList;